import api from "./axios";
import { clearReferral, getReferralCodeForSpace } from "../utils/referral";

/**
 * Keepz checkout.
 *
 * Every call returns a hosted checkout URL to redirect to — card details are entered
 * on Keepz's page, never here. The amount is always decided server-side from the
 * product's own pricing; nothing here sends a price.
 *
 * No method is sent: the buyer chooses on Keepz's page, which is the only place Apple
 * Pay and Google Pay exist. The server still accepts a method key — it maps one to a
 * Keepz provider and refuses one that isn't allowed — if we ever offer the choice here
 * again. @see mintly-back/src/services/keepz/paymentMethods
 *
 * @see mintly-back/src/routes/keepzRoutes
 */

/** The body fields every checkout shares. The personal number is only sent for installments. */
const choiceBody = ({ method, payer } = {}) => ({
  ...(method ? { method } : {}),
  ...(payer ? { payer } : {}),
});

/**
 * Start paying for a product.
 * @param {string} mintSpaceId
 * @param {string} [productId] - omit only when the space sells exactly one product
 * @param {{ method?: string, payer?: { personalNumber: string, isForeign: boolean } }} [choice]
 * @param {string} [promoCode] - lowers the price; one that takes off all of it grants
 *   access with no payment (`granted`)
 * @returns {Promise<{ free?: true, granted?: true, checkoutUrl?: string, orderId?: string, amount?: number, currency?: string, isSubscription?: boolean, frameable?: boolean }>}
 */
export const startProductCheckout = async (mintSpaceId, productId, choice, promoCode, bookingId) => {
  const referralCode = getReferralCodeForSpace(mintSpaceId);
  const response = await api.post("/keepz/checkout/product", {
    mintSpaceId,
    productId,
    referralCode,
    ...(bookingId ? { bookingId } : {}),
    ...(promoCode ? { promoCode } : {}),
    ...choiceBody(choice),
  });
  // The code rides along with the order and is spent when it opens — keeping it would
  // misattribute the next space they buy from.
  if (referralCode && (response.data?.checkoutUrl || response.data?.granted)) clearReferral();
  return response.data;
};

/**
 * What a product costs with a promo code, checked as checkout would check it. A code
 * that can't be used is refused with a `code` saying why (expired, used_up, …).
 * @returns {Promise<{ code, discountType, discountValue, price, discountAmount, finalPrice, currency, isSubscription }>}
 */
export const quotePromoCode = async (mintSpaceId, productId, promoCode) => {
  const response = await api.post("/keepz/checkout/promo", { mintSpaceId, productId, promoCode });
  return response.data?.data;
};

/** Top up the signed-in user's wallet balance. */
export const startTopUpCheckout = async (amount, choice) => {
  const response = await api.post("/keepz/checkout/topup", {
    amount,
    ...choiceBody(choice),
  });
  return response.data;
};

/** Fund a campaign's creator-rewards budget. */
export const startCampaignFundCheckout = async (campaignId, amount, choice) => {
  const response = await api.post("/keepz/checkout/campaign", {
    campaignId,
    amount,
    ...choiceBody(choice),
  });
  return response.data;
};

/**
 * Current state of an order. Polling this also nudges the server to re-check with
 * Keepz, so the flow completes even if the payment callback never arrives.
 */
export const getOrderStatus = async (orderId) => {
  const response = await api.get(`/keepz/orders/${encodeURIComponent(orderId)}`);
  return response.data;
};

/** Abandon an order that hasn't been paid. */
export const cancelOrder = async (orderId) => {
  const response = await api.post(`/keepz/orders/${encodeURIComponent(orderId)}/cancel`);
  return response.data;
};

/** Refund a purchase — mint space owner or moderator only. */
export const refundOrder = async (orderId, amount) => {
  const response = await api.post(
    `/keepz/orders/${encodeURIComponent(orderId)}/refund`,
    amount != null ? { amount } : {}
  );
  return response.data;
};

/** Everything the signed-in user has bought. */
export const getMyPurchases = async () => {
  const response = await api.get("/keepz/purchases");
  return response.data;
};

/** Sales for a mint space — owner and moderators only. */
export const getSpaceSales = async (mintSpaceId) => {
  const response = await api.get(`/keepz/mint-spaces/${mintSpaceId}/sales`);
  return response.data;
};

/** The signed-in user's subscriptions. */
export const getMySubscriptions = async () => {
  const response = await api.get("/keepz/subscriptions");
  return response.data;
};

/** Cancel a subscription. Access runs to the end of the period already paid for. */
export const cancelSubscription = async (subscriptionId) => {
  const response = await api.post(`/keepz/subscriptions/${subscriptionId}/cancel`);
  return response.data;
};

/** Does the signed-in user have access to this product? */
export const checkProductAccess = async (mintSpaceId, productId) => {
  const path = productId
    ? `/keepz/access/${mintSpaceId}/${productId}`
    : `/keepz/access/${mintSpaceId}`;
  const response = await api.get(path);
  return response.data;
};
